import { useEffect, useMemo, useRef, useState } from "react";
import { Heart, LayoutGrid } from "lucide-react";
import { CatalogCard } from "@/components/catalog/CatalogCard";
import { CatalogToolbar } from "@/components/catalog/CatalogToolbar";
import { applyFilters, emptyState } from "@/lib/catalogFilters";

const BATCH = 12;

function useInfinite(total, resetKey) {
  const [count, setCount] = useState(BATCH);
  const ref = useRef(null);
  useEffect(() => { setCount(BATCH); }, [resetKey]);
  useEffect(() => {
    const el = ref.current;
    if (!el || count >= total) return undefined;
    const obs = new IntersectionObserver((entries) => {
      if (entries[0].isIntersecting) setCount((c) => Math.min(c + BATCH, total));
    }, { rootMargin: "400px" });
    obs.observe(el);
    return () => obs.disconnect();
  }, [count, total]);
  return [count, ref];
}

const LABEL = { school: ["schools", "school"], program: ["programs", "program"] };

function ViewSwitch({ type, view, setView, total, saved }) {
  const btn = (key, Icon, text) => (
    <button data-testid={`view-${type}-${key}`} onClick={() => setView(key)}
      className={`inline-flex items-center gap-1.5 px-4 h-10 rounded-full text-sm font-semibold transition-colors ${view === key ? "bg-navy text-white" : "text-navy hover:bg-white"}`}>
      <Icon className="h-3.5 w-3.5" fill={key === "saved" && view === key ? "currentColor" : "none"} /> {text}
    </button>
  );
  return (
    <div className="inline-flex p-1 rounded-full bg-secondary mb-5">
      {btn("all", LayoutGrid, `All ${LABEL[type][0]} (${total})`)}
      {btn("saved", Heart, `My favorites (${saved})`)}
    </div>
  );
}

export function CatalogSection({ type, items, favs, favIds, onToggle, onAskAI, busy }) {
  const [view, setView] = useState("all");
  const [state, setState] = useState(emptyState);
  const source = view === "saved" ? favs : items;
  const shown = useMemo(() => applyFilters(source, state, type), [source, state, type]);
  const [count, sentinel] = useInfinite(shown.length, `${view}|${JSON.stringify(state)}`);
  const visible = shown.slice(0, count);

  return (
    <div data-testid={`section-${type}`}>
      <ViewSwitch type={type} view={view} setView={setView} total={items.length} saved={favs.length} />
      <CatalogToolbar type={type} items={source} state={state} setState={setState} />
      <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground mb-4" data-testid={`results-count-${type}`}>
        {shown.length > visible.length ? `${visible.length} of ${shown.length} ${LABEL[type][0]} shown` : `${shown.length} ${shown.length === 1 ? LABEL[type][1] : LABEL[type][0]}`}
      </p>
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
        {visible.map((item) => (
          <CatalogCard key={item.external_id} item={item} isFav={favIds.has(item.external_id)} onToggle={(it) => onToggle(it, type)}
            onAskAI={view === "saved" ? onAskAI : undefined} disabled={busy} />
        ))}
      </div>
      {visible.length < shown.length && (
        <div ref={sentinel} className="flex justify-center py-8" data-testid={`load-more-${type}`}>
          <span className="h-6 w-6 rounded-full border-2 border-orange border-t-transparent animate-spin" />
        </div>
      )}
      {shown.length === 0 && (
        <div className="text-center py-16" data-testid={`empty-${type}`}>
          <Heart className="h-12 w-12 text-muted-foreground/30 mx-auto mb-3" />
          <p className="text-muted-foreground">
            {view === "saved" && !favs.length ? `No favorite ${LABEL[type][0]} yet. Tap the heart on any ${LABEL[type][1]}.` : "No results match your filters."}
          </p>
        </div>
      )}
    </div>
  );
}
